import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { COMPANY } from '../../data/homeContent';
import logo from '../../assets/bsplnewlogo-removebg-preview.png';

const Footer = () => {
  const year = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Portfolio', path: '/portfolio' },
    { label: 'Contact', path: '/contact' },
  ];

  const linkedInPath =
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z';

  return (
    <footer className="relative bg-bspl-dark text-white overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 w-64 h-64 bg-bspl-accent/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-bspl-blue/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-6">
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-3 group">
              <div className="w-14 h-14 flex items-center justify-center shrink-0">
                <img
                  src={logo}
                  alt={COMPANY.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-white group-hover:text-bspl-accent transition-colors">
                {COMPANY.name}
              </h3>
            </Link>
            <p className="text-gray-400 text-sm mb-4 max-w-md leading-relaxed">
              We build modern, scalable digital products for growing businesses across India and
              worldwide.
            </p>
            <a
              href={COMPANY.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="inline-flex w-9 h-9 bg-gray-800 rounded-full items-center justify-center text-gray-400 hover:bg-bspl-accent hover:text-white transition-colors duration-300"
            >
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                <path d={linkedInPath} />
              </svg>
            </a>
          </div>

          <div>
            <h4 className="font-display text-base font-semibold mb-3 text-white">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map(({ label, path }) => (
                <li key={path}>
                  <Link
                    to={path}
                    className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group text-sm"
                  >
                    <span className="group-hover:translate-x-1 transition-transform text-bspl-accent">
                      →
                    </span>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-base font-semibold mb-3 text-white">Contact Info</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-start gap-2 hover:text-white transition-colors"
                >
                  <Mail size={16} strokeWidth={1.5} className="text-bspl-accent shrink-0 mt-0.5" />
                  {COMPANY.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  className="flex items-start gap-2 hover:text-white transition-colors"
                >
                  <Phone size={16} strokeWidth={1.5} className="text-bspl-accent shrink-0 mt-0.5" />
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} strokeWidth={1.5} className="text-bspl-accent shrink-0 mt-0.5" />
                {COMPANY.location}
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-gray-400 text-sm text-center md:text-left">
              &copy; {year} {COMPANY.name}. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-bspl-accent transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-bspl-accent transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
